import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane,
  faSpinner,
  faUser,
  faShieldAlt,
  faHeadset,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@iconify/react';
import { useChatMessages } from '../../hooks/useChatMessages';
import { format, isSameDay, differenceInMinutes } from 'date-fns';
import Avatar from '../ui/Avatar';
import { logger } from '../../utils/logger';
import { supabase } from '../../lib/supabaseClient';
import { useUserProfilesByIds, normalizeAvatarUrl } from '../../hooks/useUserProfiles';

interface ChatWindowProps {
  conversationId: string;
  conversationTitle?: string;
  onClose?: () => void;
  className?: string;
}

const roleIcons = {
  admin: faShieldAlt,
  agent: faHeadset,
  user: faUser,
  carrier: faTruck,
};

const roleColors = {
  admin: 'bg-purple-100 text-purple-700 border-purple-200',
  agent: 'bg-blue-100 text-blue-700 border-blue-200',
  user: 'bg-gray-100 text-gray-700 border-gray-200',
  carrier: 'bg-green-100 text-green-700 border-green-200',
};

interface MessageGroup {
  sender_id: string;
  sender_name: string;
  sender_email: string;
  sender_role: 'admin' | 'agent' | 'user' | 'carrier';
  sender_avatar_url?: string | null;
  messages: Array<{
    id: string;
    message: string;
    created_at: string;
    is_read: boolean;
    sender_avatar_url?: string | null;
  }>;
  firstMessageTime: string;
  showAvatar: boolean;
  showTimestamp: boolean;
  showRoleBadge: boolean;
}

export default function ChatWindow({
  conversationId,
  conversationTitle,
  onClose,
  className = '',
}: ChatWindowProps) {
  const [message, setMessage] = useState('');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const {
    messages,
    isLoading,
    error,
    sendMessageAsync,
    isSending,
    markAsRead,
  } = useChatMessages({ conversationId });

  // Get current user ID
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUserId(user?.id || null);
    });
  }, []);

  // Get unique sender IDs from messages to fetch their profiles
  // Only fetch profiles for messages that don't have sender_avatar_url (backward compatibility)
  const senderIds = useMemo(() => {
    return Array.from(new Set(
      messages
        .filter((msg) => !msg.sender_avatar_url) // Only fetch if avatar_url is missing
        .map((msg) => msg.sender_id)
    ));
  }, [messages]);

  // Fetch user profiles for senders without avatar_url (cached with React Query)
  // Messages with sender_avatar_url will use that directly
  const { data: userProfilesMap = new Map() } = useUserProfilesByIds(senderIds);

  // Group consecutive messages from the same sender
  const messageGroups = useMemo(() => {
    if (messages.length === 0) return [];

    const groups: MessageGroup[] = [];
    let currentGroup: MessageGroup | null = null;
    let lastMessageTime: Date | null = null;

    messages.forEach((msg) => {
      const msgTime = new Date(msg.created_at);
      const isSameSender = currentGroup && currentGroup.sender_id === msg.sender_id;
      const timeDiff = lastMessageTime ? differenceInMinutes(msgTime, lastMessageTime) : Infinity;

      // Start new group if:
      // - First message
      // - Different sender
      // - More than 5 minutes passed
      if (!isSameSender || timeDiff > 5) {
        // Save previous group
        if (currentGroup) {
          groups.push(currentGroup);
        }

        // Create new group
        currentGroup = {
          sender_id: msg.sender_id,
          sender_name: msg.sender_name,
          sender_email: msg.sender_email,
          sender_role: msg.sender_role,
          sender_avatar_url: msg.sender_avatar_url,
          messages: [{
            id: msg.id,
            message: msg.message,
            created_at: msg.created_at,
            is_read: msg.is_read,
            sender_avatar_url: msg.sender_avatar_url,
          }],
          firstMessageTime: msg.created_at,
          showAvatar: true, // Always show avatar for first message in group
          showTimestamp: true, // Always show timestamp for first message in group
          showRoleBadge: true, // Show role badge only on first message of group
        };
      } else {
        // Add to current group
        if (currentGroup) {
          currentGroup.messages.push({
            id: msg.id,
            message: msg.message,
            created_at: msg.created_at,
            is_read: msg.is_read,
            sender_avatar_url: msg.sender_avatar_url,
          });
        }
      }

      lastMessageTime = msgTime;
    });

    // Add last group
    if (currentGroup) {
      groups.push(currentGroup);
    }

    return groups;
  }, [messages]);



  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      const container = messagesContainerRef.current;
      const scrollToBottom = () => {
        container.scrollTop = container.scrollHeight;
      };
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [messages]);

  // Mark messages as read when viewing (only messages from others)
  useEffect(() => {
    if (messages.length > 0 && currentUserId) {
      const unreadIds = messages
        .filter((msg) => !msg.is_read && msg.sender_id !== currentUserId)
        .map((msg) => msg.id);
      if (unreadIds.length > 0) {
        markAsRead(unreadIds);
      }
    }
  }, [messages, currentUserId, markAsRead]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isSending) return;

    const messageToSend = message.trim();
    setMessage('');

    try {
      await sendMessageAsync(messageToSend);
      logger.log('✅ Message sent successfully');
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    } catch (err) {
      logger.error('❌ Error sending message:', err);
      setMessage(messageToSend);
      alert('Error sending message. Please try again.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (error) {
    const isNetworkError = error instanceof Error && (error as any).isNetworkError;
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-red-600 font-medium mb-2">
          {isNetworkError ? 'Connection issue' : 'Error loading chat'}
        </p>
        <p className="text-sm text-gray-600">
          {isNetworkError
            ? 'Please check your internet connection. The app will retry automatically.'
            : (error as Error).message || 'An unexpected error occurred'}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden ${className}`}
      style={{
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header - Compact */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
            {conversationTitle || 'Chat'}
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex items-center justify-center w-9 h-9 rounded-full text-[#003380] hover:text-[#002660] hover:bg-gray-100 transition-colors flex-shrink-0"
            aria-label="Close chat"
          >
            <Icon icon="mdi:close" className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Messages - Modern WhatsApp style */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-2 sm:px-3 md:px-4 py-3 bg-gray-50 min-h-0 overscroll-contain relative"
        style={{
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          flex: '1 1 0%',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
        }}
      >
        {/* Background illustration - More visible */}
        <div className="absolute inset-0 opacity-[0.08] pointer-events-none overflow-hidden z-0">
          <img
            src="/core/cyan-blur.png"
            alt=""
            className="w-full h-full object-cover"
            style={{ transform: 'scale(1.3)' }}
          />
        </div>
        <div className="relative z-10">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-blue-600 animate-spin" />
              <span className="ml-2 text-gray-600 text-sm">Loading messages...</span>
            </div>
          ) : messageGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center relative overflow-hidden">
              {/* Background illustration */}
              <div className="absolute inset-0 opacity-5 pointer-events-none">
                <img
                  src="/core/cyan-blur.png"
                  alt=""
                  className="w-full h-full object-cover"
                  style={{ transform: 'scale(1.5)' }}
                />
              </div>
              <div className="relative z-10">
                <img
                  src="/core/placeholder.svg"
                  alt="No messages"
                  className="w-32 h-32 mx-auto mb-4 opacity-30"
                />
                <p className="text-gray-500 text-sm font-medium">No messages yet</p>
                <p className="text-xs text-gray-400 mt-1">Start the conversation!</p>
              </div>
            </div>
          ) : (
            <>
              {messageGroups.map((group, groupIndex) => {
                const isOwnMessage = group.sender_id === currentUserId;
                const userProfile = userProfilesMap.get(group.sender_id);
                // Use sender_avatar_url from group if available (preferred - already normalized in DB)
                // Otherwise fallback to profile avatar_url (normalize it if needed)
                // This ensures avatars are always available even if profile cache is cleared
                const avatarUrl = group.sender_avatar_url || (userProfile?.avatar_url ? normalizeAvatarUrl(userProfile.avatar_url) : null);
                const prevGroup = groupIndex > 0 ? messageGroups[groupIndex - 1] : null;
                const showDateSeparator = !prevGroup || !isSameDay(new Date(group.firstMessageTime), new Date(prevGroup.firstMessageTime));

                return (
                  <React.Fragment key={`group-${groupIndex}`}>
                    {/* Date separator */}
                    {showDateSeparator && (
                      <div className="flex items-center justify-center my-4">
                        <div className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                          {format(new Date(group.firstMessageTime), 'MMMM d, yyyy')}
                        </div>
                      </div>
                    )}

                    <div className={`flex gap-2 mb-1 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                      {/* Avatar - Only shown for first message in group */}
                      <div className="flex-shrink-0" style={{ width: group.showAvatar ? '32px' : '0px', visibility: group.showAvatar ? 'visible' : 'hidden' }}>
                        {group.showAvatar && (
                          <Avatar
                            src={avatarUrl}
                            name={userProfile?.full_name || group.sender_name}
                            email={group.sender_email}
                            size="sm"
                          />
                        )}
                      </div>

                      {/* Messages in group */}
                      <div className={`flex-1 max-w-[75%] sm:max-w-[70%] md:max-w-[65%] ${isOwnMessage ? 'items-end' : 'items-start'} flex flex-col gap-0.5`}>
                        {/* Name and role badge - Only on first message (always show for incoming messages) */}
                        {group.showRoleBadge && !isOwnMessage && (
                          <div className="flex items-center gap-2 mb-0.5 px-1">
                            <span className="text-xs font-semibold text-gray-700">
                              {userProfile?.full_name || group.sender_name}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1 ${roleColors[group.sender_role]}`}>
                              <FontAwesomeIcon
                                icon={roleIcons[group.sender_role]}
                                className="w-2 h-2"
                              />
                              <span className="capitalize">{group.sender_role}</span>
                            </span>
                          </div>
                        )}

                        {/* Message bubbles */}
                        {group.messages.map((msg, msgIndex) => {
                          const isLastInGroup = msgIndex === group.messages.length - 1;
                          const showTime = group.showTimestamp && isLastInGroup;

                          // Determine bubble color based on sender role
                          const isIncomingAdmin = !isOwnMessage && group.sender_role === 'admin';
                          const isIncomingAgent = !isOwnMessage && group.sender_role === 'agent';

                          return (
                            <div
                              key={msg.id}
                              className={`rounded-2xl px-3 py-2 ${isOwnMessage
                                ? 'bg-blue-500 text-white rounded-br-sm'
                                : isIncomingAdmin
                                  ? 'bg-purple-500 text-white rounded-bl-sm'
                                  : isIncomingAgent
                                    ? 'bg-indigo-500 text-white rounded-bl-sm'
                                    : 'bg-white text-gray-900 rounded-bl-sm shadow-sm'
                                }`}
                              style={{
                                borderRadius: isOwnMessage
                                  ? '18px 18px 4px 18px'
                                  : '18px 18px 18px 4px',
                              }}
                            >
                              <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">
                                {msg.message}
                              </p>
                              {showTime && (
                                <p className={`text-[10px] mt-1 ${isOwnMessage
                                  ? 'text-blue-100'
                                  : isIncomingAdmin || isIncomingAgent
                                    ? 'text-purple-100'
                                    : 'text-gray-400'
                                  }`}>
                                  {format(new Date(msg.created_at), 'HH:mm')}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input - Compact and always visible */}
      <form
        onSubmit={handleSubmit}
        className="p-2 sm:p-3 border-t border-gray-200 bg-white flex-shrink-0"
        style={{
          position: 'relative',
          bottom: 0,
          left: 0,
          right: 0,
        }}
      >
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={(e) => {
              if (window.innerWidth < 768) {
                setTimeout(() => {
                  e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
                }, 300);
              }
            }}
            placeholder="Type a message..."
            className="flex-1 resize-none rounded-full border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] max-h-[120px]"
            rows={1}
            maxLength={2000}
            disabled={isSending}
            style={{
              overflowY: 'auto',
            }}
          />
          <button
            type="submit"
            disabled={!message.trim() || isSending}
            className="w-10 h-10 bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center flex-shrink-0 shadow-lg hover:shadow-xl hover:shadow-blue-500/50 hover:scale-110 active:scale-95 disabled:hover:scale-100 disabled:hover:shadow-lg ring-2 ring-blue-500/20 hover:ring-blue-500/40"
            aria-label="Send message"
          >
            {isSending ? (
              <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
            ) : (
              <FontAwesomeIcon icon={faPaperPlane} className="w-4 h-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
